import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUserStats } from '../context/UserStatsContext';
import { useSubscription } from '../context/SubscriptionContext';
import { PLAN_NAMES } from '../types/subscription';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { stats } = useUserStats();
  const { subscription, plan, isActive, createStripePortal } = useSubscription();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const formatDate = (date: Date | null | undefined): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleManageSubscription = async () => {
    if (!subscription?.stripeCustomerId) {
      navigate('/pricing');
      return;
    }

    setIsOpeningPortal(true);
    try {
      const portalUrl = await createStripePortal(window.location.href);
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      setIsOpeningPortal(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE' || !currentUser) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${currentUser.uid}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }

      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const getPlanBadgeColor = (planType: string): string => {
    switch (planType) {
      case 'pro_interview':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300';
      case 'pro':
        return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300';
      case 'basic':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getStatusBadgeColor = (status: string | undefined): string => {
    switch (status) {
      case 'active':
      case 'trialing':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'canceled':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
      case 'past_due':
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to App
          </button>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-white">My Profile</h1>
          <div className="w-24" /> {/* Spacer for centering */}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* User Info Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/50 p-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Account Information
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Email</span>
              <span className="text-gray-800 dark:text-white font-medium">{currentUser?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Member since</span>
              <span className="text-gray-800 dark:text-gray-200">{formatDate(stats?.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Subscription Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/50 p-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Subscription
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Current Plan</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlanBadgeColor(plan)}`}>
                {PLAN_NAMES[plan]}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Status</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(subscription?.status)}`}>
                {subscription?.status ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) : 'Active'}
              </span>
            </div>
            {subscription?.billingCycle && plan !== 'free' && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">Billing Cycle</span>
                <span className="text-gray-800 dark:text-gray-200">
                  {subscription.billingCycle === 'annual' ? 'Annual' : 'Monthly'}
                </span>
              </div>
            )}
            {subscription?.currentPeriodEnd && plan !== 'free' && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">
                  {subscription?.cancelAtPeriodEnd ? 'Access until' : 'Next billing date'}
                </span>
                <span className="text-gray-800 dark:text-gray-200">{formatDate(subscription.currentPeriodEnd)}</span>
              </div>
            )}
            {subscription?.cancelAtPeriodEnd && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mt-2">
                <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                  Your subscription will be canceled at the end of the current billing period.
                </p>
              </div>
            )}
          </div>
          <div className="mt-6 flex gap-3">
            {plan === 'free' ? (
              <button
                onClick={() => navigate('/pricing')}
                className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
              >
                Upgrade Plan
              </button>
            ) : (
              <button
                onClick={handleManageSubscription}
                disabled={isOpeningPortal}
                className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {isOpeningPortal ? 'Opening...' : 'Manage Subscription'}
              </button>
            )}
            <button
              onClick={() => navigate('/pricing')}
              className="flex-1 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>

        {/* Practice Stats Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/50 p-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Practice Statistics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                {formatTime(stats?.totalPracticeMinutes || 0)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Practice</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                {stats?.pronunciationSessions || 0}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Pronunciation Sessions</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                {stats?.interviewSessions || 0}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Interview Sessions</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                {stats?.currentStreak || 0}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Day Streak</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Avg. Pronunciation Score</span>
              <span className="text-gray-800 dark:text-white font-medium">
                {stats?.averagePronunciationScore ? `${Math.round(stats.averagePronunciationScore)}%` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Avg. Interview Score</span>
              <span className="text-gray-800 dark:text-white font-medium">
                {stats?.averageInterviewScore ? `${stats.averageInterviewScore.toFixed(1)}/10` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Longest Streak</span>
              <span className="text-gray-800 dark:text-white font-medium">{stats?.longestStreak || 0} days</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Last Practice</span>
              <span className="text-gray-800 dark:text-gray-200">{formatDate(stats?.lastPracticeDate)}</span>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/50 p-6 border border-red-200 dark:border-red-700">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Danger Zone
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Once you delete your account, there is no going back. All your data will be permanently removed.
          </p>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            Delete Account
          </button>
        </div>
      </main>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Delete Account</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
            </p>
            {subscription && plan !== 'free' && isActive && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mb-4">
                <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                  Your active subscription will be canceled immediately and you will not be refunded.
                </p>
              </div>
            )}
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              Please type <span className="font-mono font-bold">DELETE</span> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            {deleteError && (
              <p className="text-red-600 dark:text-red-400 text-sm mb-4">{deleteError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setDeleteError(null);
                }}
                className="flex-1 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
